import { useEffect, useMemo, useState } from 'react';

import {
  normalizeOption,
  type NormalizedOption
} from './useCostCalculatorState';
import type { CostCalculatorBreed } from '../data/costCalculatorBreeds';
import type {
  CalculatorInput,
  ResultsListItem
} from '../components/resultsPanel.types';

export type DisplayedTotals = Record<
  string,
  { annual: string; monthly: string }
>;

export type InputKind = 'breed' | 'age' | 'location' | 'other';

export const kindFromInput = (input: CalculatorInput): InputKind => {
  const legacy = (input.legacyId ?? '').toLowerCase();
  if (legacy === 'breed') return 'breed';
  if (legacy === 'age') return 'age';
  if (legacy === 'location') return 'location';

  const key = `${input.name ?? ''} ${input.label ?? ''}`.toLowerCase();
  if (key.includes('breed')) return 'breed';
  if (key.includes('age')) return 'age';
  if (key.includes('location')) return 'location';
  return 'other';
};

export const normalizeOptions = (
  options: Array<Record<string, unknown> | null | undefined> | undefined
): NormalizedOption[] =>
  (options ?? [])
    .map((o) => normalizeOption(o))
    .filter((o): o is NormalizedOption => Boolean(o));

export const findOptionByToken = (
  options: NormalizedOption[],
  token: string
) => {
  const t = token.toLowerCase();
  return (
    options.find((o) => o.value.toLowerCase() === t) ??
    options.find((o) => o.title.toLowerCase() === t) ??
    options.find(
      (o) =>
        o.value.toLowerCase().includes(t) || o.title.toLowerCase().includes(t)
    )
  );
};

export const isSelectedValue = (
  current: unknown,
  option: NormalizedOption
): boolean => {
  if (current === null || current === undefined) return false;
  return String(current).toLowerCase() === option.value.toLowerCase();
};

export const buildBreedOptions = (breeds: CostCalculatorBreed[]) =>
  breeds.map((b) => ({ value: b.name, label: b.name }));

export const findBreedByToken = (
  breeds: CostCalculatorBreed[],
  raw: unknown
): CostCalculatorBreed | null => {
  if (typeof raw !== 'string' || raw.trim().length === 0) return null;
  const token = raw.toLowerCase();
  return (
    breeds.find((b) => b.name.toLowerCase() === token) ??
    breeds.find((b) => b.slug.toLowerCase() === token) ??
    null
  );
};

const formatMoney = (value: number) => value.toFixed(2);

export const useAnimatedTotals = (
  hasResults: boolean,
  resultsList: ResultsListItem[]
): DisplayedTotals => {
  const resultAnnualMap = useMemo(() => {
    const map: Record<string, number> = {};
    resultsList.forEach((result) => {
      map[result.id] = result.value ?? 0;
    });
    return map;
  }, [resultsList]);

  const [displayedTotals, setDisplayedTotals] = useState<DisplayedTotals>({});

  useEffect(() => {
    if (!hasResults) {
      setDisplayedTotals({});
      return;
    }

    const start = performance.now();
    const durationMs = 1000;
    let raf: number | null = null;

    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / durationMs, 1);

      const next: DisplayedTotals = {};
      resultsList.forEach((result) => {
        const annualTarget = resultAnnualMap[result.id] ?? 0;
        const annual = progress * annualTarget;
        const monthly = annual / 12;
        next[result.id] = {
          annual: formatMoney(annual),
          monthly: formatMoney(monthly)
        };
      });

      setDisplayedTotals(next);

      if (progress < 1) {
        raf = requestAnimationFrame(animate);
      }
    };

    raf = requestAnimationFrame(animate);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [hasResults, resultAnnualMap, resultsList]);

  return displayedTotals;
};
